import { orgPaymentPortalReturnedEvent } from '#pplx/frontend/apps/ask/events/org-management/analytics.js';
import { getAsiAccessDecision } from '#pplx/frontend/apps/ask/queries/billing-queries/credits/getAsiAccessDecision.js';
import { getRefreshOrgSubscriptionStatus } from '#pplx/frontend/apps/ask/queries/enterprise-queries/getRefreshOrgSubscriptionStatus.js';
import { getUserOrganization } from '#pplx/frontend/apps/ask/queries/enterprise-queries/getUserOrganization.js';
import { getUserOrgDataKey } from '#pplx/frontend/apps/ask/queries/query-keys/enterpriseKeys.js';
import { invalidateSubscriptionQueries } from '#pplx/frontend/apps/ask/queries/upsell-queries/invalidateSubscriptionQueries.js';
import { SafeSessionStorage } from '#pplx/frontend/browser-storage/sessionStorage.js';
import { ORG_ONBOARDING_SUCCESS_REDIRECT_KEY } from '#pplx/frontend/components/utils/consts.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
// The org's billing settle is webhook-driven and can land tens of seconds
// after the checkout redirect, so the immediate invalidate below may re-cache
// a pre-settle "no computer access" decision for its full staleTime. Poll the
// decision uncached on a short backoff and drop the subscription caches again
// once it settles (or once, best-effort, after the last attempt).
const SETTLE_POLL_DELAYS_MS = [5_000, 15_000, 30_000];
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const refreshSubscriptionQueriesAfterSettle = async (queryClient) => {
    for (const delayMs of SETTLE_POLL_DELAYS_MS) {
        await delay(delayMs);
        const decision = await getAsiAccessDecision({ reason: 'org-checkout-settle-poll' });
        if (decision?.can_use_computer)
            break;
    }
    invalidateSubscriptionQueries(queryClient);
};
export const useOrgRefreshSubscription = ({ reason }) => {
    const queryClient = useQueryClient();
    const router = useRouter();
    const queryParams = useSearchParams();
    const stripeCheckout = queryParams?.get('stripe_checkout');
    const isEnterprise = queryParams?.get('is_enterprise') === 'true';
    const { track } = useInstrumentation();
    const isStripeCheckout = stripeCheckout !== undefined && stripeCheckout !== null;
    const isStripeCheckoutSuccess = stripeCheckout === 'success';
    // Prevent duplicate checkout-return side effects if session-backed deps change
    // before the Stripe return query params are removed.
    const [hasHandledStripeCheckoutReturn, setHasHandledStripeCheckoutReturn] = useState(false);
    useEffect(() => {
        const refreshSubscription = async () => {
            try {
                const [_refreshedSubscription, userOrgData] = await Promise.all([
                    // Only refresh subscription if stripe checkout is successful
                    isStripeCheckoutSuccess
                        ? queryClient.fetchQuery({
                            queryKey: QueryClientHelper.makeQueryKey('/rest/enterprise/organization/subscription/refresh'),
                            queryFn: () => getRefreshOrgSubscriptionStatus({ reason }),
                        })
                        : null,
                    queryClient
                        .fetchQuery({
                        queryKey: getUserOrgDataKey(),
                        queryFn: () => getUserOrganization({ reason }),
                    })
                        .catch((error) => {
                        // The organization read is ancillary to checkout completion. Keep its
                        // cache in error without skipping billing invalidation or the redirect.
                        logger.log('Failed to refresh organization after checkout:', error);
                        return undefined;
                    }),
                ]);
                if (isStripeCheckoutSuccess) {
                    // Drop the pre-checkout subscription-dependent caches (incl.
                    // asi-access-decision) so Computer access reflects the new
                    // subscription without waiting out staleTime. Mirrors the consumer
                    // path in StripeStatusRefresher.
                    invalidateSubscriptionQueries(queryClient);
                    // Fire-and-forget: don't block the success redirect on the settle poll.
                    void refreshSubscriptionQueriesAfterSettle(queryClient).catch((error) => {
                        logger.log('Failed to refresh org subscription after settle:', error);
                    });
                }
                const orgUuid = userOrgData?.organization?.uuid;
                if (orgUuid != null && orgUuid !== undefined) {
                    // Track portal returned for stripe checkout success and cancel
                    track(orgPaymentPortalReturnedEvent({
                        name: 'org payment portal returned',
                        data: {
                            stripeResult: stripeCheckout ?? '',
                            orgUUID: orgUuid ?? '',
                            source: queryParams?.get('origin') ?? '',
                        },
                    }));
                }
                if (isStripeCheckoutSuccess) {
                    const redirectPath = SafeSessionStorage.getItem(ORG_ONBOARDING_SUCCESS_REDIRECT_KEY);
                    if (redirectPath) {
                        SafeSessionStorage.removeItem(ORG_ONBOARDING_SUCCESS_REDIRECT_KEY);
                        router.push(redirectPath, undefined, 'org onboarding success redirect');
                    }
                }
            }
            catch (error) {
                logger.log('Failed to refresh org subscription:', error);
            }
        };
        if (isStripeCheckout && isEnterprise && !hasHandledStripeCheckoutReturn) {
            setHasHandledStripeCheckoutReturn(true);
            refreshSubscription();
        }
    }, [
        hasHandledStripeCheckoutReturn,
        isEnterprise,
        isStripeCheckout,
        isStripeCheckoutSuccess,
        queryClient,
        queryParams,
        reason,
        router,
        stripeCheckout,
        track,
    ]);
};
//# sourceMappingURL=useOrgRefreshSubscription.js.map